import { AfterViewInit, Component, OnInit, ViewChild } from "@angular/core";
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { Router } from "@angular/router";
import { Observable } from "rxjs";

import { ApplicationConstantsEnum } from "../../../core/enums/applications-constants.enum";
import { ChannelTypeEnum } from "../../../core/enums/chanel.type.enum";
import { DocumentTypeTapEnum } from "../../../core/enums/document-type-tap.enum";
import { MessageEnum } from "../../../core/enums/message.enum";
import { OverflowErrorsEnum } from "../../../core/enums/overflow-errors.enum";

import { ClientEducationalService } from "../../../services/local/client-educational.service";
import { CustomerService } from "../../../services/local/customer.service";
import { FileManagerService } from "../../../services/local/file-manager.service";
import { OverflowErrorsService } from "../../../services/local/overflow-errors.service";
import { PayrollService } from "../../../services/local/payroll.service";
import { TealiumUtagService } from "../../../tealium/utag.service";

import { FormFileInputDragComponent } from "../../groups/form-file-input-drag/form-file-input-drag.component";

@Component({
  selector: "app-form-upload",
  templateUrl: "./form-upload.component.html",
})
export class FormUploadComponent implements OnInit, AfterViewInit {
  @ViewChild("fileInputDrag", { static: false })
  fileInputDrag: FormFileInputDragComponent;

  @ViewChild("fileInputDragCertified", { static: false })
  fileInputDragCertified: FormFileInputDragComponent;

  description: string;
  documentChannel = new Map();
  file: File;
  isClientEducational: boolean;
  isPreloadedLaborCertification: boolean = true;
  isPreloadedPayrollCheck: boolean;
  loadingUploadButton: boolean;
  messageBox: boolean;
  numberAttemptsError: number;
  progress = new Map();
  progressError: boolean;
  steps: any;
  title: string;
  uploadedDocuments: any[] = [];

  documentTypeTapEnum = DocumentTypeTapEnum;
  formGroup: UntypedFormGroup;
  messageEnum: MessageEnum;
  observableUploadFile: Observable<any>;

  constructor(
    private clientEducationalService: ClientEducationalService,
    private customerService: CustomerService,
    private fileManagerService: FileManagerService,
    private payrollService: PayrollService,
    private overflowErrorsService: OverflowErrorsService,
    private router: Router,
    private tealiumUtagService: TealiumUtagService,
  ) {
    this.documentChannel.set(
      DocumentTypeTapEnum.LABOR_CERTIFICATION,
      ChannelTypeEnum.LABOR_CERTIFICATION,
    );
    this.documentChannel.set(
      DocumentTypeTapEnum.PAYROLLCHECK,
      ChannelTypeEnum.PAYROLLCHECK,
    );
  }

  ngOnInit() {
    this.title = "Cargue de documentos";
    this.description = "Recuerda que no deben superar los 1 MB";
    this.isClientEducational =
      this.clientEducationalService.isEducationalSector();
    this.numberAttemptsError = 0;
    this.steps = {
      numberSteps: 2,
      stepActive: 1,
    };

    this.initPreloadDocuments();
    this.initForm();
  }

  ngAfterViewInit(): void {
    this.tealiumUtagService.view({
      tealium_event: "page_view",
      page_path: document.location.pathname,
    });
  }

  fileUpload(
    file: File,
    fileInputDrag: FormFileInputDragComponent,
    payrollFileType: DocumentTypeTapEnum,
  ) {
    const channel = this.documentChannel.get(payrollFileType);

    this.file = file;

    if (this.fileManagerService.validateFile(file)) {
      this.loadingUploadButton = true;

      this.fileManagerService.sendFile(file, channel).subscribe(
        (progress: number) => {
          this.progress.set(payrollFileType, progress);
        },
        () => {
          this.router.navigate(["overflowerror"]);
        },
      );
    } else {
      this.errorUploadFile(fileInputDrag.title);
    }
  }

  fileInputComplete(
    fileInputDrag: FormFileInputDragComponent,
    payrollFileType: DocumentTypeTapEnum,
  ) {
    const customer = this.customerService.getCustomer();
    if (fileInputDrag.fileName) {
      this.tealiumUtagService.link({
        tealium_event: "click",
        event_category: "carga_documentos",
        event_label: "seleccionar_archivo",
        userId: this.tealiumUtagService.managementUserId(),
      });

      this.uploadedDocuments.push({
        file: this.file,
        name: payrollFileType,
        title: fileInputDrag.title,
        url: customer.urlFile,
      });
    }
    this.loadingUploadButton = false;
  }

  closeMessageBox() {
    this.messageBox = false;
  }

  async continue() {
    this.loadingUploadButton = true;
    let isSuccessSend: boolean = true;

    this.tealiumUtagService.link({
      tealium_event: "click",
      event_category: "carga_documentos",
      event_label: "finalizar",
      userId: this.tealiumUtagService.managementUserId(),
    });

    await new Promise((resolve) => {
      this.uploadedDocuments.forEach((data: any) => {
        this.payrollService
          .sendFileTap(data.file, data.name, data.url)
          .subscribe(
            () => {
              resolve(true);
            },
            () => {
              isSuccessSend = false;
              this.errorUploadFile(data.title);
              return;
            },
          );
      });
    });

    if (isSuccessSend) {
      this.loadingUploadButton = false;
      this.payrollService.cleanSession();
      this.router.navigate(["confirmation"]);
    }
  }

  private errorUploadFile(fileInputDrag: string) {
    this.numberAttemptsError++;

    if (this.numberAttemptsError === ApplicationConstantsEnum.NUMBER_ATTEMPTS) {
      this.overflowErrorsService.setOverflowError(
        OverflowErrorsEnum.EXCEEDED_MAXIMUM_NUMBER_ATTEMPTS_CUSTOMER,
      );

      this.payrollService.cleanSession();
      this.router.navigate(["customer/overflowerror"]);
    }

    this.loadingUploadButton = false;

    this.showErrorUploadFile(fileInputDrag);

    this.messageBox = true;
    this.messageEnum = MessageEnum.ERROR_UPLOAD_FILE_TAP_SOLUTION;
  }

  private initForm() {
    this.formGroup = new UntypedFormGroup({});

    if (this.isPreloadedPayrollCheck === false) {
      this.formGroup.addControl(
        "filePayrollCheck",
        new UntypedFormControl("", Validators.required),
      );
    }

    if (this.isPreloadedLaborCertification === false) {
      this.formGroup.addControl(
        "fileCertified",
        new UntypedFormControl("", Validators.required),
      );
    }
  }

  private initPreloadDocuments() {
    this.isPreloadedPayrollCheck = this.isPreloadedDocument(
      DocumentTypeTapEnum.PAYROLLCHECK,
    );

    if (this.clientEducationalService.isEducationalSector()) {
      this.isPreloadedLaborCertification = this.isPreloadedDocument(
        DocumentTypeTapEnum.LABOR_CERTIFICATION,
      );
    }
  }

  private isPreloadedDocument(documentTypeTapEnum: string) {
    const listFilePreloaded =
      this.clientEducationalService.getListFileDocuments();

    if (listFilePreloaded.length > ApplicationConstantsEnum.EMPTY_ARRAY) {
      const documentPreloaded = listFilePreloaded.find((list: any) => {
        return documentTypeTapEnum === list.document.onbaseCode;
      });

      return documentPreloaded.preloaded;
    }
    return false;
  }

  private showErrorUploadFile(fileInputDrag: string) {
    return this.fileInputDragCertified !== undefined &&
      fileInputDrag === this.fileInputDragCertified.title
      ? this.fileInputDragCertified.showErrorUploadFile()
      : this.fileInputDrag.showErrorUploadFile();
  }
}
